import {
  type ApiError,
  fetchReadiness,
  getControlLockSnapshot,
  normalizeList,
  orchestrator,
  readManualTransfer,
  readManualTransferOptions,
  readScheduler,
  type StopsList,
  useAcquireControlLock,
  useCancelModuleExecution,
  useClearSafety,
  useConfirmUnload,
  useForgetModuleGrips,
  useInitializeModule,
  usePrepareModules,
  useRecoverBatch,
  useReleaseStops,
  useResetModuleRecoveryLatch,
  useResetScheduler,
  useRunModuleSequence,
  useStartManualTransfer,
  useStartModuleRecovery,
  useStopBatch,
} from '@ctcells/api'
import type { Alarm, Batch, ModuleSnapshot, SafetyState, TrayUnload } from '@ctcells/contract'
import { CONFIRM_SOURCE_BY_SURFACE } from '@ctcells/core'
import { errorMessage, moduleTypeKey, moduleTypeLabel, serverText } from '@ctcells/i18n'

import { ACTIVE_BATCH_LIMIT } from '../../shell/activeBatchSource.ts'
import { prepareRunGate } from '../admin/prepareModulesFlow.ts'
import type { ScreenAccess } from '../access.ts'

import type { QuickResetPorts } from './devQuickReset.ts'

/**
 * 자동 준비(`devQuickReset.ts`)가 부르는 **실제 창구** — 명령은 기존 명령 훅의 `mutateAsync` 를 그대로 쓴다.
 * 본문 모양 · `command_id`(매번 새로) · `X-Control-Token`(`controlToken.ts` 표) · 캐시 무효화가 전부 그 훅에서 온다.
 * 🔴 여기서 본문을 새로 짓지 않는다 — 같은 창구의 정의처는 훅 하나다.
 *
 * ⚠ **조회는 훅이 아니라 한 번씩 직접 받는다**(`orchestrator.request`). 이 흐름은 단계마다 «지금» 서버 값을
 *   다시 읽어야 하고(DP-04 — 보낸 명령의 결과는 다시 읽은 값이 말한다), 조회 훅을 버튼에 마운트하면 목록 화면을
 *   열 때마다 요청이 는다. 단발 조회라 주기 폴링이 아니다 — 반복은 IDLE 대기 단계 안에서만 한다.
 */

const LIST_LIMIT = { limit: ACTIVE_BATCH_LIMIT }

async function listed<T>(path: string, query: Record<string, string | number | boolean>) {
  return normalizeList<T>(await orchestrator.request({ method: 'GET', path, query }), LIST_LIMIT)
    .items
}

/** 실패 사유 — 사전 문구 + **에러 코드 원문**(eric 2026-09-21 「영문 코드 … 일단 노출」). 코드가 없으면 HTTP 상태 */
export function quickFailureText(error: ApiError): string {
  const code = error.error_code ?? (error.status > 0 ? `HTTP ${String(error.status)}` : undefined)
  const message = errorMessage(error)
  return code === undefined ? message : `${message} (${code})`
}

export function useQuickResetPorts(access: ScreenAccess): QuickResetPorts {
  const acquire = useAcquireControlLock()
  const stop = useStopBatch()
  const recover = useRecoverBatch()
  const releaseStops = useReleaseStops()
  const confirmUnload = useConfirmUnload()
  const prepare = usePrepareModules()
  const cancel = useCancelModuleExecution()
  const initialize = useInitializeModule()
  const sequence = useRunModuleSequence()
  const resetScheduler = useResetScheduler()
  const placeHeld = useStartManualTransfer()
  const resetLatch = useResetModuleRecoveryLatch()
  const startRecovery = useStartModuleRecovery()
  const clearSafety = useClearSafety()
  const forgetGrips = useForgetModuleGrips()

  return {
    // 🔴 루트 `/readyz` — 던지지 않는다. HTTP 상태가 아니라 본문 `checks` 를 돌려준다
    readReadiness: fetchReadiness,
    // 🔴 제어락 헤더는 전송 계층이 붙인다(`controlToken.ts` 표 — 쥐었을 때만) · `reset_allowed` 가 그 값으로 선다
    readScheduler,
    resetScheduler: async (reason) => {
      await resetScheduler.mutateAsync({ reason })
    },
    /*
     * 🔴 보관소의 «지금» 값이다 — 이 흐름은 비동기 한가운데서 락을 집고 곧바로 다음 단계로 간다.
     *    렌더 때 받은 `useControlLock()` 값은 그 사이 낡는다(아직 다시 그리지 않았다).
     */
    lockHeld: () => getControlLockSnapshot().status === 'mine',
    // 🔴 단발 조회 — 화면의 안전 구독(`/safety/stream`)과 별개로 «지금» 서버 값을 읽는다(DP-04)
    readSafety: () => orchestrator.request<SafetyState>({ method: 'GET', path: '/safety' }),
    // 본문은 훅이 짓는다(`{command_id}` — 서버는 그 밖의 칸을 읽지 않는다 · `views/safety.py` `clear_safety`)
    clearSafety: () => clearSafety.mutateAsync({}),
    acquireLock: async () => {
      await acquire.mutateAsync({})
    },
    listActiveBatches: () => listed<Batch>('/batches', { ...LIST_LIMIT, phase: 'active' }),
    readBatch: (batchId) =>
      orchestrator.request<Batch>({ method: 'GET', path: `/batches/${batchId}` }),
    stopBatch: async (batchId, reason) => {
      await stop.mutateAsync({ batchId, reason })
    },
    discardBatch: async (batchId, reason) => {
      await recover.mutateAsync({ batchId, decision: 'discard', reason })
    },
    // 🔴 단발 조회 — 쪽 나눔 없는 `StopsList{items, alarms_open}` 라 `normalizeList` 를 거치지 않는다
    listStops: async () =>
      (await orchestrator.request<StopsList>({ method: 'GET', path: '/safety/stops' })).items,
    /*
     * 🔴 `physicalChecked` 는 참 — 「테스트 작업 시작」은 무엇이 걸려 있든 초기화하고 시작한다(eric 2026-10-21 「테스트 작업 시작하면.. 상관없이
     *    초기화하고. 무조건 시작하라고 했잖아」). 서버가 풀 항목 종류마다 현장 확인을 요구하게 바뀌어(`physical_checks` · 빠지면 422) 거짓으로는
     *    이송 격리 해제가 늘 막혔다. 옛 칸 참 = 확인 셋(door_area · robot_area · tray_positions) 모두(`views/stops.py`). 로컬 개발 장비 전용 흐름이다
     */
    releaseStops: (ids, reason) => releaseStops.mutateAsync({ ids, reason, physicalChecked: true }),
    listPendingUnloads: () => listed<TrayUnload>('/unloads', { pending: true }),
    confirmUnload: async (unloadId) => {
      const source = CONFIRM_SOURCE_BY_SURFACE.console
      if (source === null) throw new Error('confirm_source')
      await confirmUnload.mutateAsync({ unloadId, confirmSource: source })
    },
    prepareBlocked: (activeBatches) => {
      const decided = prepareRunGate(
        { ...access, lockHeld: getControlLockSnapshot().status === 'mine' },
        { activeBatches, activeBatchesLoading: false },
        { preparing: false, canSend: true },
      )
      return decided.disabled ? decided.disabledReason : undefined
    },
    prepare: async (reason) => {
      await prepare.mutateAsync({ reason })
    },
    listModules: () => listed<ModuleSnapshot>('/modules', {}),
    listActiveAlarms: () => listed<Alarm>('/alarms', { ...LIST_LIMIT, state: 'active' }),
    // 🔴 활성 + 확인됨 — 확인된 행은 재발해도 `acknowledged` 에 남는다(`QuickResetPorts.listRobotAlarms`)
    listRobotAlarms: async (moduleId) => {
      const [active, acknowledged] = await Promise.all([
        listed<Alarm>('/alarms', { ...LIST_LIMIT, source: moduleId, state: 'active' }),
        listed<Alarm>('/alarms', { ...LIST_LIMIT, source: moduleId, state: 'acknowledged' }),
      ])
      return [...active, ...acknowledged]
    },
    cancelExecution: async (moduleId, executionId, reason) => {
      await cancel.mutateAsync({ moduleId, executionId, reason })
    },
    initialize: async (moduleId, options) => {
      await initialize.mutateAsync({
        moduleId,
        ...(options?.rotorStopConfirmed === true ? { rotorStopConfirmed: true } : {}),
      })
    },
    readPlaceOptions: readManualTransferOptions,
    placeHeld: (destPointId, reason) =>
      placeHeld.mutateAsync({ kind: 'place_held', destPointId, reason }),
    readManualTransfer,
    resetRecoveryLatch: async (moduleId, reason) => {
      await resetLatch.mutateAsync({ moduleId, reason })
    },
    startRecovery: async (moduleId, reason) => {
      await startRecovery.mutateAsync({ moduleId, reason })
    },
    forgetGrips: async (moduleId, robotId, reason) => {
      await forgetGrips.mutateAsync({ moduleId, robotId, reason })
    },
    runSequence: async (moduleId, name, params) => {
      const accepted = await sequence.mutateAsync({ moduleId, sequence: name, params })
      return accepted.execution_id
    },
    moduleName: (module) => moduleTypeLabel(module.module_type) ?? module.module_id,
    moduleReasonName: (module) => {
      const key = moduleTypeKey(module.module_type)
      return key === undefined ? module.module_id : serverText(`module.${key}`)
    },
    failure: (error) => quickFailureText(error as ApiError),
    sleep: (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms)
      }),
    now: () => Date.now(),
  }
}
